import React from 'react';
import PropTypes from 'prop-types';

import {
  PrsAlertRoot,
  PrsAlertIcon,
  PrsAlertContent,
  PrsAlertClose,
} from './PrsAlertStyles';
import { frontEndUrl } from '../../destructured-front-end-config';

// Default icon per variant — variant carries semantic meaning, so the matching
// glyph belongs on the alert itself, not on every call site. Consumers can still
// override by passing an explicit `icon` prop (or pass `icon={null}` to suppress).
const VARIANT_ICON_URLS = {
  success: `${frontEndUrl}static/images/check_circle.svg`,
  danger: `${frontEndUrl}static/images/error-circle.svg`,
  warning: `${frontEndUrl}static/images/report_problem.svg`,
};

const CLOSE_ICON_URL = `${frontEndUrl}static/images/close_icon.svg`;

/**
 * Prose v1.5 alert. Variant-driven chrome (info/success/warning/danger/neutral),
 * optional ghost mod, default icon per variant, optional trailing close button.
 *
 * Pure presentational — positioning, auto-dismiss, and live-region semantics
 * belong on the consumer (see UamToast for the toast use case).
 */
// danger = assistive-tech "alert" + assertive announcement (interrupts).
// success/warning/info/neutral = polite "status" (queues behind in-progress speech).
const variantRoleDefaults = (variant) => {
  if (variant === 'danger') return { role: 'alert', ariaLive: 'assertive' };
  if (variant) return { role: 'status', ariaLive: 'polite' };
  return { role: null, ariaLive: null };
};

const PrsAlert = ({
  variant, ghost, icon, onClose, closeAriaLabel, role, ariaLive, className, children,
}) => {
  const resolvedIcon = icon === undefined && VARIANT_ICON_URLS[variant]
    ? <img src={VARIANT_ICON_URLS[variant]} alt="" width="24" height="24" />
    : icon;

  const defaults = variantRoleDefaults(variant);
  const resolvedRole = role || defaults.role;
  const resolvedAriaLive = ariaLive || defaults.ariaLive;

  return (
    <PrsAlertRoot
      $variant={variant}
      $ghost={ghost}
      role={resolvedRole}
      aria-live={resolvedAriaLive || undefined}
      aria-atomic={resolvedAriaLive ? 'true' : undefined}
      className={className}
    >
      {resolvedIcon && <PrsAlertIcon>{resolvedIcon}</PrsAlertIcon>}
      <PrsAlertContent>{children}</PrsAlertContent>
      {onClose && (
        <PrsAlertClose type="button" onClick={onClose} aria-label={closeAriaLabel}>
          <img src={CLOSE_ICON_URL} alt="" width="16" height="16" />
        </PrsAlertClose>
      )}
    </PrsAlertRoot>
  );
};

PrsAlert.defaultProps = {
  variant: null,
  ghost: false,
  icon: undefined,
  onClose: null,
  closeAriaLabel: 'Dismiss',
  role: null,
  ariaLive: null,
  className: null,
  children: null,
};

PrsAlert.propTypes = {
  variant: PropTypes.oneOf(['info', 'success', 'warning', 'danger', 'neutral']),
  ghost: PropTypes.bool,
  icon: PropTypes.node,
  onClose: PropTypes.func,
  closeAriaLabel: PropTypes.string,
  role: PropTypes.string,
  ariaLive: PropTypes.oneOf(['off', 'polite', 'assertive']),
  className: PropTypes.string,
  children: PropTypes.node,
};

export default PrsAlert;
